import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, FileText, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RequestCase() {
  const { user } = useAuth();
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (user) fetchMyRequests();
  }, [user]);

  const fetchMyRequests = async () => {
    try {
      const res = await axios.get(`${API}/my-case-requests`);
      setMyRequests(res.data);
    } catch (err) {
      toast.error('Failed to load your requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      toast.error('Please enter a description for your case request');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/case-requests`, { description });
      toast.success('Case request submitted successfully!');
      setDescription('');
      fetchMyRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const statusColors = {
    pending: 'bg-amber-100 text-amber-800',
    in_review: 'bg-blue-100 text-blue-800',
    fulfilled: 'bg-green-100 text-green-800',
    declined: 'bg-red-100 text-red-800'
  };

  const statusLabels = {
    pending: 'Pending',
    in_review: 'In Review',
    fulfilled: 'Fulfilled',
    declined: 'Declined'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="pt-24 flex justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="request-case-page">
      <Navbar />
      <div className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-serif font-bold tracking-tight text-slate-900 mb-2">Request a Case</h1>
          <p className="text-slate-600 mb-10">Can't find the case study you need? Describe what you're looking for and our team will work on it.</p>

          <div className="bg-white rounded-xl border border-slate-200 p-8 mb-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Describe the case you need *</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={6}
                  placeholder='Example: "I am looking for a case in Operations Management in the specific context of pharmaceutical sector in African context."'
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent resize-none"
                  data-testid="case-request-description"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50"
                data-testid="submit-case-request-btn"
              >
                <Send className="w-4 h-4" />
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </form>
          </div>

          {myRequests.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4">My Requests</h2>
              <div className="space-y-4">
                {myRequests.map(req => (
                  <div key={req.id} className="bg-white rounded-xl border border-slate-200 p-6" data-testid={`request-${req.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(req.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-slate-700">{req.description}</p>
                        {req.admin_notes && (
                          <p className="mt-2 text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">
                            <strong>Admin Notes:</strong> {req.admin_notes}
                          </p>
                        )}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusColors[req.status] || 'bg-slate-100 text-slate-700'}`}>
                        {statusLabels[req.status] || req.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
